import { useEffect, useRef } from 'react';
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import { useAppStore } from '../state/store';
import type { OhlcByInstrument, ChartId, ChartTimeSyncPreset } from '../state/store';
import {
  getCanonicalTimestampsForAllCharts,
  getSplitChartData,
  getIndicatorValuesOnTimeline,
  padSeriesToTimeline,
} from '../lib/chartTimeline';
import { useSyncedCrosshair } from '../hooks/useSyncedCrosshair';
import { splitAnnotationPlugin } from './splitAnnotationPlugin';
import { tradeMarkersPlugin } from './tradeMarkersPlugin';
import {
  getChartBehavior,
  getIndicatorChartScale,
  getIndicatorDisplayInfo,
  getIndicatorDisplayName,
} from '../utils/indicatorDisplayRegistry';
import { applyRange, RANGE_PRESETS } from '../lib/chartRangePresets';

const UP_COLOR = '#1a1a1a';
const DOWN_COLOR = '#8b3a3a';

/** Right y-axis size in px. All time-aligned charts must use this so plot area widths match. */
export const CHART_RIGHT_AXIS_SIZE = 58;

/**
 * Maps a backtest bar index to the primary chart's canonical timeline index.
 * Used so trade centering and markers use chart index space when primary/secondary
 * instruments have different date ranges.
 */
function backendBarToChartIndex(
  backendBarIndex: number,
  backendTimestamps: string[],
  chartTimestamps: number[]
): number {
  if (backendBarIndex < 0 || backendBarIndex >= backendTimestamps.length)
    return backendBarIndex;
  const iso = backendTimestamps[backendBarIndex];
  const dateOnly = typeof iso === 'string' ? iso.slice(0, 10) : '';
  const unix = Math.floor(new Date(dateOnly + 'T00:00:00Z').getTime() / 1000);
  const idx = chartTimestamps.findIndex((t) => t === unix);
  if (idx >= 0) return idx;
  let best = 0;
  let bestDiff = Math.abs(chartTimestamps[0] - unix);
  for (let i = 1; i < chartTimestamps.length; i++) {
    const d = Math.abs(chartTimestamps[i] - unix);
    if (d < bestDiff) {
      bestDiff = d;
      best = i;
    }
  }
  return best;
}

function formatIndexAsDate(timestamps: number[], idx: number): string {
  const i = Math.max(0, Math.min(timestamps.length - 1, Math.round(idx)));
  const sec = timestamps[i];
  if (sec == null) return '';
  const d = new Date(sec * 1000);
  const m = d.getUTCMonth() + 1;
  const day = d.getUTCDate();
  const y = d.getUTCFullYear();
  return `${m}/${day}/${y}`;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_ABBREV_UPPER = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

function datePartsAt(timestamps: number[], idx: number): { y: number; m: number; d: number } | null {
  const i = Math.max(0, Math.min(timestamps.length - 1, Math.round(idx)));
  const sec = timestamps[i];
  if (sec == null) return null;
  const d = new Date(sec * 1000);
  return {
    y: d.getUTCFullYear(),
    m: d.getUTCMonth(),
    d: d.getUTCDate(),
  };
}

/** Hierarchical x-axis label: year at year change, "Jan YYYY" / "MMM" at month change, day or M/D within month. */
function hierarchicalAxisLabel(timestamps: number[], idx: number): string {
  const i = Math.max(0, Math.min(timestamps.length - 1, Math.round(idx)));
  const cur = datePartsAt(timestamps, i);
  if (!cur) return '';
  const prev = i > 0 ? datePartsAt(timestamps, i - 1) : null;
  const yearChange = !prev || cur.y !== prev.y;
  const monthChange = !prev || cur.m !== prev.m;
  if (yearChange) return String(cur.y);
  if (monthChange) return cur.m === 0 ? `${MONTH_NAMES[0]} ${cur.y}` : MONTH_NAMES[cur.m];
  return `${cur.m + 1}/${cur.d}`;
}

/** Day-of-month only (for top row when mode is dayMonthTwoRow). */
function dayOfMonthLabel(timestamps: number[], idx: number): string {
  const cur = datePartsAt(timestamps, Math.round(idx));
  return cur ? String(cur.d) : '';
}

/** Day row in dayMonthTwoRow: show day number except at month boundaries (month is on second row). */
function dayOfMonthLabelSkipMonthBoundary(timestamps: number[], idx: number): string {
  const i = Math.round(idx);
  if (isMonthBoundary(timestamps, i)) return '';
  return dayOfMonthLabel(timestamps, idx);
}

/** Year only (for mode yearOnly). */
function yearOnlyLabel(timestamps: number[], idx: number): string {
  const cur = datePartsAt(timestamps, Math.round(idx));
  return cur ? String(cur.y) : '';
}

// Range-based x-axis rules (no button dependency)
const BARS_90D = 63;
const YEARS_3 = 3;
const MIN_X_TICKS_FILL_IN = 3;
const MAX_X_TICKS_DENSE = 15;
const MAX_TICKS_DAY_MODE = 45;

export type XAxisMode = 'yearOnly' | 'yearMonth' | 'dayMonthTwoRow';

/** Find bar index for a given timestamp (exact or nearest). */
export function findIndexForTimestamp(
  timestamps: number[],
  ts: number
): number | null {
  if (timestamps.length === 0) return null;
  const i = timestamps.indexOf(ts);
  if (i >= 0) return i;
  let best = 0;
  let bestDiff = Math.abs(timestamps[0] - ts);
  for (let i = 1; i < timestamps.length; i++) {
    const d = Math.abs(timestamps[i] - ts);
    if (d < bestDiff) {
      bestDiff = d;
      best = i;
    }
  }
  return best;
}

export function getXAxisMode(
  timestamps: number[],
  scaleMin: number,
  scaleMax: number
): XAxisMode {
  const iMin = Math.max(0, Math.floor(scaleMin));
  const iMax = Math.min(timestamps.length - 1, Math.ceil(scaleMax));
  const visibleBars = iMax - iMin + 1;
  const minY = datePartsAt(timestamps, iMin)?.y;
  const maxY = datePartsAt(timestamps, iMax)?.y;
  const yearsSpanned = minY != null && maxY != null ? maxY - minY + 1 : 1;

  if (yearsSpanned >= YEARS_3) return 'yearOnly';
  if (visibleBars <= BARS_90D) return 'dayMonthTwoRow';
  return 'yearMonth';
}

function isMonthBoundary(timestamps: number[], idx: number): boolean {
  if (idx <= 0) return true;
  const cur = datePartsAt(timestamps, idx);
  const prev = datePartsAt(timestamps, idx - 1);
  return cur != null && prev != null && cur.m !== prev.m;
}

/** Second-row label at month change: "02-FEB" for month-only, "JAN 2026" when year also changes. */
function monthChangeLabel(timestamps: number[], idx: number): string {
  const cur = datePartsAt(timestamps, idx);
  if (!cur) return '';
  const prev = idx > 0 ? datePartsAt(timestamps, idx - 1) : null;
  const yearChange = !prev || cur.y !== prev.y;
  const monthAbbrev = MONTH_ABBREV_UPPER[cur.m];
  if (yearChange) return `${monthAbbrev} ${cur.y}`;
  const dayPadded = String(cur.d).padStart(2, '0');
  return `${dayPadded}-${monthAbbrev}`;
}

/**
 * Splits for index-based x-axis. Behavior depends only on visible range (getXAxisMode):
 * - yearOnly: only year boundaries
 * - yearMonth: year + month boundaries, fill-in if <3, thin months if >15
 * - dayMonthTwoRow: many ticks (every 2nd bar), cap at MAX_TICKS_DAY_MODE
 */
export function xAxisSplits(
  timestamps: number[],
  scaleMin: number,
  scaleMax: number
): number[] {
  const iMin = Math.max(0, Math.floor(scaleMin));
  const iMax = Math.min(timestamps.length - 1, Math.ceil(scaleMax));
  const visibleBars = iMax - iMin + 1;
  const mode = getXAxisMode(timestamps, scaleMin, scaleMax);

  const yearBoundaries: number[] = [];
  const monthBoundaries: number[] = [];

  for (let i = iMin; i <= iMax; i++) {
    const cur = datePartsAt(timestamps, i);
    if (!cur) continue;
    const prev = i > 0 ? datePartsAt(timestamps, i - 1) : null;
    if (!prev || cur.y !== prev.y) yearBoundaries.push(i);
    else if (cur.m !== prev.m) monthBoundaries.push(i);
  }

  if (mode === 'yearOnly') {
    return yearBoundaries;
  }

  if (mode === 'dayMonthTwoRow') {
    const step = Math.max(1, Math.floor(visibleBars / MAX_TICKS_DAY_MODE));
    const splits: number[] = [];
    for (let i = iMin; i <= iMax; i += step) splits.push(i);
    if (splits.length > 0 && splits[splits.length - 1] !== iMax) splits.push(iMax);
    return splits;
  }

  // yearMonth: current behavior
  let splits = [...yearBoundaries, ...monthBoundaries].sort((a, b) => a - b);

  if (splits.length < MIN_X_TICKS_FILL_IN && visibleBars >= MIN_X_TICKS_FILL_IN) {
    const step = Math.max(1, Math.floor(visibleBars / MIN_X_TICKS_FILL_IN));
    const set = new Set(splits);
    for (let i = iMin; i <= iMax; i += step) set.add(i);
    splits = Array.from(set).sort((a, b) => a - b);
  }

  if (splits.length > MAX_X_TICKS_DENSE) {
    const yearSet = new Set(yearBoundaries);
    const monthOnly = splits.filter((idx) => !yearSet.has(idx));
    const keepMonthCount = Math.max(0, MAX_X_TICKS_DENSE - yearBoundaries.length);
    const step = monthOnly.length <= keepMonthCount ? 1 : Math.ceil(monthOnly.length / keepMonthCount);
    const thinnedMonths = monthOnly.filter((_, j) => j % step === 0);
    splits = [...yearBoundaries, ...thinnedMonths].sort((a, b) => a - b);
  }
  return splits;
}

/** X-axis config (size, splits, values) shared with other charts (e.g. equity) for aligned ticks and labels. */
export function createXAxisConfig(timestamps: number[]): uPlot.Axis {
  return {
    size: (u) => {
      const mode = getXAxisMode(timestamps, u.scales.x.min ?? 0, u.scales.x.max ?? 0);
      return mode === 'dayMonthTwoRow' ? 56 : 30;
    },
    splits: (_u, _axisIdx, scaleMin, scaleMax) =>
      xAxisSplits(timestamps, scaleMin, scaleMax),
    values: (u, vals) => {
      const mode = getXAxisMode(timestamps, u.scales.x.min ?? 0, u.scales.x.max ?? 0);
      const labelFn =
        mode === 'yearOnly'
          ? yearOnlyLabel
          : mode === 'dayMonthTwoRow'
            ? dayOfMonthLabelSkipMonthBoundary
            : hierarchicalAxisLabel;
      return vals.map((v) =>
        v != null ? labelFn(timestamps, Math.round(v as number)) : ''
      );
    },
  };
}

function getNum(row: Record<string, number>, ...keys: string[]): number {
  for (const k of keys) {
    const v = row[k] ?? row[k.toLowerCase()];
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
  }
  return 0;
}

/** Parse date string YYYY-MM-DD or ISO to Unix seconds (start of day UTC). */
function toUnixSeconds(ts: string): number {
  const s = ts.slice(0, 10);
  return Math.floor(new Date(s + 'T00:00:00Z').getTime() / 1000);
}

export type UplotChartData = {
  data: uPlot.AlignedData;
  timestamps: number[];
};

/**
 * Returns the same primary timestamps used to build the primary chart's canonical timeline
 * (filtered: open > 0 && close > 0). Use this when building the equity chart's canonical
 * so both charts share the exact same timeline.
 */
export function getPrimaryTimestampsForCanonical(
  ohlc: OhlcByInstrument | null,
  primaryInstrumentId: string | null
): number[] {
  if (!ohlc || !primaryInstrumentId) return [];
  const raw = buildUplotData(ohlc, primaryInstrumentId);
  return raw?.timestamps ?? [];
}

/**
 * Build uPlot data with index-based x (one bar per trading day).
 * data[0] = indices [0, 1, ..., n-1]; timestamps used only for axis labels.
 * When adjusted is true, uses Adj_* columns; otherwise uses Open/High/Low/Close/Volume.
 */
export function buildUplotData(
  ohlc: OhlcByInstrument,
  instrumentId: string,
  adjusted: boolean = true
): UplotChartData | null {
  const inst = ohlc[instrumentId];
  if (!inst?.series) return null;

  const openKey = adjusted ? 'Adj_Open' : 'Open';
  const highKey = adjusted ? 'Adj_High' : 'High';
  const lowKey = adjusted ? 'Adj_Low' : 'Low';
  const closeKey = adjusted ? 'Adj_Close' : 'Close';
  const volKey = adjusted ? 'Adj_Volume' : 'Volume';
  const fallbackOpen = adjusted ? 'Open' : 'Adj_Open';
  const fallbackHigh = adjusted ? 'High' : 'Adj_High';
  const fallbackLow = adjusted ? 'Low' : 'Adj_Low';
  const fallbackClose = adjusted ? 'Close' : 'Adj_Close';
  const fallbackVol = adjusted ? 'Volume' : 'Adj_Volume';

  const entries = Object.entries(inst.series)
    .map(([ts, row]) => ({
      t: toUnixSeconds(ts),
      open: getNum(row, openKey, fallbackOpen),
      high: getNum(row, highKey, fallbackHigh),
      low: getNum(row, lowKey, fallbackLow),
      close: getNum(row, closeKey, fallbackClose),
      vol: getNum(row, volKey, fallbackVol),
    }))
    .filter((d) => d.open > 0 && d.close > 0);

  entries.sort((a, b) => a.t - b.t);
  if (entries.length === 0) return null;

  const n = entries.length;
  const timestamps = entries.map((e) => e.t);
  const data: uPlot.AlignedData = [
    Array.from({ length: n }, (_, i) => i),
    entries.map((e) => Math.round(e.open * 100) / 100),
    entries.map((e) => Math.round(e.high * 100) / 100),
    entries.map((e) => Math.round(e.low * 100) / 100),
    entries.map((e) => Math.round(e.close * 100) / 100),
    entries.map((e) => Math.round(e.vol)),
  ];
  return { data, timestamps };
}

/** Bottom fraction of chart for volume strip; shared with AdditionalInstrumentChart for split annotation alignment. */
export const VOLUME_STRIP_RATIO = 0.2;

const PRICE_RANGE_PAD = 0.02; // 2% padding above/below visible price range

/** Compute min/max over scale-'y' series only for indices [iMin, iMax]. */
function priceExtentOverRange(
  u: uPlot,
  iMin: number,
  iMax: number
): { min: number; max: number } {
  let min = Infinity;
  let max = -Infinity;
  for (let si = 1; si < u.series.length; si++) {
    if (u.series[si].scale !== 'y') continue;
    const arr = u.data[si] as (number | null)[];
    if (!arr) continue;
    for (let i = iMin; i <= iMax; i++) {
      const v = arr[i];
      if (v != null && Number.isFinite(v)) {
        if (v < min) min = v;
        if (v > max) max = v;
      }
    }
  }
  return { min, max };
}

/** Format extent with padding or flat band; returns [0, 1] if extent is invalid. */
function formatPriceRange(min: number, max: number): [number, number] {
  if (min === Infinity || max === -Infinity) return [0, 1];
  if (min === max) {
    const band = min !== 0 ? Math.abs(min) * 0.01 : 1;
    return [min - band, max + band];
  }
  const pad = (max - min) * PRICE_RANGE_PAD;
  return [min - pad, max + pad];
}

/** Min/max price (low/high) over the visible x range for y-scale, with padding.
 * Pass explicit xMin/xMax when u.scales.x may not be updated yet (e.g. right after applyRange on init).
 * Fallbacks use scale-'y' data over the full timeline only (never dataMin/dataMax) so the price scale is never driven by volume. */
export function visiblePriceRange(
  u: uPlot,
  _dataMin?: number,
  _dataMax?: number,
  xMin?: number,
  xMax?: number
): [number, number] {
  const n = u.data[0].length - 1;
  const scaleMin = xMin ?? u.scales.x.min;
  const scaleMax = xMax ?? u.scales.x.max;
  if (scaleMin == null || scaleMax == null || !Number.isFinite(scaleMin) || !Number.isFinite(scaleMax)) {
    const { min, max } = priceExtentOverRange(u, 0, n);
    return formatPriceRange(min, max);
  }
  const iMin = Math.max(0, Math.floor(scaleMin));
  const iMax = Math.min(n, Math.ceil(scaleMax));
  if (iMin > iMax) {
    const { min, max } = priceExtentOverRange(u, 0, n);
    return formatPriceRange(min, max);
  }
  const { min, max } = priceExtentOverRange(u, iMin, iMax);
  if (min === Infinity || max === -Infinity) {
    const { min: fullMin, max: fullMax } = priceExtentOverRange(u, 0, n);
    return formatPriceRange(fullMin, fullMax);
  }
  return formatPriceRange(min, max);
}

/** Max volume over the visible x range for vol scale; range [0, max * 1.1].
 * Pass explicit xMin/xMax when u.scales.x may not be updated yet. */
export function visibleVolRange(
  u: uPlot,
  _dataMin?: number,
  dataMax?: number,
  xMin?: number,
  xMax?: number
): [number, number] {
  const n = u.data[0].length - 1;
  const scaleMin = xMin ?? u.scales.x.min;
  const scaleMax = xMax ?? u.scales.x.max;
  if (scaleMin == null || scaleMax == null || !Number.isFinite(scaleMin) || !Number.isFinite(scaleMax)) {
    return dataMax != null && dataMax > 0 ? [0, dataMax * 1.1] : [0, 1];
  }
  const iMin = Math.max(0, Math.floor(scaleMin));
  const iMax = Math.min(n, Math.ceil(scaleMax));
  if (iMin > iMax) {
    return dataMax != null && dataMax > 0 ? [0, dataMax * 1.1] : [0, 1];
  }
  let max = 0;
  for (let si = 1; si < u.series.length; si++) {
    if (u.series[si].scale !== 'vol') continue;
    const arr = u.data[si] as (number | null)[];
    if (!arr) continue;
    for (let i = iMin; i <= iMax; i++) {
      const v = arr[i];
      if (v != null && Number.isFinite(v) && v > max) max = v;
    }
  }
  return max <= 0 ? [0, 1] : [0, max * 1.1];
}

const MIN_BAR_WIDTH = 2;
const MAX_BAR_WIDTH = 10;
const BAR_WIDTH_FACTOR = 0.5;
// Slightly thinner bars when view is ~180 days (~95–145 trading days)
const BAR_COUNT_180D_MIN = 95;
const BAR_COUNT_180D_MAX = 145;
const BAR_WIDTH_FACTOR_180D = 0.78;
const MIN_TICK_LENGTH = 2;
const MAX_TICK_LENGTH = 8;
const TICK_LENGTH_FACTOR = 0.25;
const TICK_THICKNESS = 2;
// Thicker open/close ticks for 180 days and smaller windows (fewer bars)
const TICK_THICKNESS_SMALL_WINDOW = 4;

/**
 * uPlot plugin: OHLC bars (vertical line low→high, open/close ticks) + volume bars in bottom strip.
 * Bar width and tick length scale with pixels per bar and are capped for readability.
 * Data order: [time, open, high, low, close, volume].
 */
export function ohlcBarsPlugin(opts: {
  upColor?: string;
  downColor?: string;
} = {}) {
  const upColor = opts.upColor ?? UP_COLOR;
  const downColor = opts.downColor ?? DOWN_COLOR;

  function draw(u: uPlot) {
    u.ctx.save();

    const { top, height, width } = u.bbox;
    const priceHeight = height * (1 - VOLUME_STRIP_RATIO);
    const priceBottom = top + priceHeight;
    const volStripTop = priceBottom;
    const volStripHeight = height * VOLUME_STRIP_RATIO;

    const idxs = u.series[0].idxs!;
    const iMin = idxs[0];
    const iMax = idxs[1];
    const visibleBarCount = Math.max(1, iMax - iMin + 1);
    const pixelsPerBar = width / visibleBarCount;

    let effectiveBarWidth = Math.max(
      MIN_BAR_WIDTH,
      Math.min(MAX_BAR_WIDTH, Math.round(pixelsPerBar * BAR_WIDTH_FACTOR))
    );
    if (
      visibleBarCount >= BAR_COUNT_180D_MIN &&
      visibleBarCount <= BAR_COUNT_180D_MAX
    ) {
      effectiveBarWidth = Math.max(
        MIN_BAR_WIDTH,
        Math.round(effectiveBarWidth * BAR_WIDTH_FACTOR_180D)
      );
    }
    const tickLength = Math.max(
      MIN_TICK_LENGTH,
      Math.min(MAX_TICK_LENGTH, Math.round(pixelsPerBar * TICK_LENGTH_FACTOR))
    );
    const effectiveTickThickness =
      visibleBarCount <= BAR_COUNT_180D_MAX
        ? TICK_THICKNESS_SMALL_WINDOW
        : TICK_THICKNESS;
    const offset = (effectiveBarWidth % 2) / 2;
    u.ctx.translate(offset, offset);

    // Remap y from full bbox to price area only
    const toPriceY = (fullBboxY: number) =>
      top + ((fullBboxY - top) / height) * priceHeight;
    // Remap y from full bbox to volume strip only
    const toVolY = (fullBboxY: number) =>
      volStripTop + ((fullBboxY - top) / height) * volStripHeight;

    let maxVol = 0;
    for (let i = iMin; i <= iMax; i++) {
      const v = u.data[5][i] as number | null;
      if (v != null && v > maxVol) maxVol = v;
    }
    if (maxVol <= 0) maxVol = 1;

    for (let i = iMin; i <= iMax; i++) {
      const xVal = u.scales.x.distr === 2 ? i : u.data[0][i];
      const open = u.data[1][i] as number | null;
      const high = u.data[2][i] as number | null;
      const low = u.data[3][i] as number | null;
      const close = u.data[4][i] as number | null;
      const vol = u.data[5][i] as number | null;
      if (open == null || high == null || low == null || close == null) continue;

      const timeAsX = u.valToPos(xVal, 'x', true);
      const lowAsY = toPriceY(u.valToPos(low, 'y', true));
      const highAsY = toPriceY(u.valToPos(high, 'y', true));
      const openAsY = toPriceY(u.valToPos(open, 'y', true));
      const closeAsY = toPriceY(u.valToPos(close, 'y', true));
      const color = close >= open ? upColor : downColor;

      const barX = Math.round(timeAsX - effectiveBarWidth / 2);
      const barW = Math.max(1, Math.round(effectiveBarWidth));

      // Volume bar: bottom 20% strip (draw first so OHLC and ticks sit on top)
      const volVal = vol ?? 0;
      const volPct = volVal / maxVol;
      const volBarHeight = Math.round(volPct * volStripHeight);
      if (volBarHeight > 0) {
        const volBarY = volStripTop + volStripHeight - volBarHeight;
        u.ctx.fillStyle = color;
        u.ctx.fillRect(barX, Math.round(volBarY), barW, volBarHeight);
      }

      // OHLC bar: vertical line from low to high
      const barTop = Math.min(highAsY, lowAsY);
      const barBottom = Math.max(highAsY, lowAsY);
      const barHeight = Math.max(1, barBottom - barTop);
      const barY = Math.round(barTop);
      u.ctx.fillStyle = color;
      u.ctx.fillRect(barX, barY, barW, Math.round(barHeight));

      // Open tick: horizontal segment to the left at open price
      const openY = Math.round(openAsY) - effectiveTickThickness / 2;
      u.ctx.fillStyle = color;
      u.ctx.fillRect(barX - tickLength, openY, tickLength, effectiveTickThickness);

      // Close tick: horizontal segment to the right at close price (or single tick if doji)
      if (Math.round(openAsY) !== Math.round(closeAsY)) {
        const closeY = Math.round(closeAsY) - effectiveTickThickness / 2;
        u.ctx.fillRect(barX + barW, closeY, tickLength, effectiveTickThickness);
      }
    }

    // Draw overlay series (scale 'y' in price band, scale 'vol' in volume strip)
    function drawOverlayLine(
      si: number,
      scaleKey: 'y' | 'vol',
      toY: (fullBboxY: number) => number
    ) {
      const s = u.series[si];
      if (!s || s.scale !== scaleKey) return;
      const data = u.data[si] as (number | null)[];
      if (!data) return;
      const spanGaps = s.spanGaps ?? false;
      const stroke = s.stroke;
      const strokeColor =
        typeof stroke === 'function'
          ? (stroke as (u: uPlot, seriesIdx: number) => string)(u, si)
          : (stroke as string | undefined);
      u.ctx.strokeStyle = strokeColor && typeof strokeColor === 'string' ? strokeColor : '#4a6572';
      u.ctx.lineWidth = (s.width as number) ?? 2;
      u.ctx.beginPath();
      let started = false;
      for (let j = iMin; j <= iMax; j++) {
        const xVal = u.scales.x.distr === 2 ? j : u.data[0][j];
        const yVal = data[j];
        if (yVal == null || !Number.isFinite(yVal)) {
          if (!spanGaps) started = false;
          continue;
        }
        const x = u.valToPos(xVal, 'x', true);
        const y = toY(u.valToPos(yVal, scaleKey, true));
        if (!started) {
          u.ctx.moveTo(x, y);
          started = true;
        } else {
          u.ctx.lineTo(x, y);
        }
      }
      u.ctx.stroke();
    }
    for (let si = 6; si < u.series.length; si++) {
      if (u.series[si].scale === 'y') drawOverlayLine(si, 'y', toPriceY);
    }
    for (let si = 6; si < u.series.length; si++) {
      if (u.series[si].scale === 'vol') drawOverlayLine(si, 'vol', toVolY);
    }

    u.ctx.translate(-offset, -offset);
    u.ctx.restore();
  }

  return {
    opts: (_u: uPlot, baseOpts: uPlot.Options) => {
      uPlot.assign(baseOpts, {
        cursor: { x: true, y: true, points: { show: false } },
      });
      // Only disable uPlot’s default line/point drawing for OHLC+Volume (indices 1–5);
      // overlay indicator series (6+) keep default paths so they draw as lines.
      baseOpts.series.forEach((s, i) => {
        if (i >= 1 && i <= 5 || (i >= 6 && (s.scale === 'y' || s.scale === 'vol'))) {
          s.paths = () => null;
          s.points = { show: false };
        }
      });
    },
    hooks: { draw },
  };
}

/**
 * Plugin: disables drag-to-zoom; enables horizontal pan with hand cursor.
 * Pan continues while mouse button is down even if cursor leaves the chart (document-level move/up).
 * Calls onScaleChange(min, max) when x scale changes so synced charts can update.
 * Optional onAfterPan(u, newMin, newMax) runs after x scale is set (e.g. to update y/vol on primary).
 * Optional cursorSync: on the hovered chart, sets cursor to mouse position and updates store
 * (timestamp, fraction 0-1 of visible range, hovered id). Other charts use the fraction to draw
 * the vertical line at the same relative position so crosshairs stay aligned.
 */
export type ChartCursorSyncConfig = {
  chartId: ChartId;
  getTimestampForCursor: (u: uPlot, idx: number) => number | null;
  setChartCursorTimestamp: (ts: number | null) => void;
  setChartCursorFraction: (f: number | null) => void;
  setChartHoveredBy: (id: ChartId | null) => void;
};

/** Returns cursor-sync config for use with chartPanPlugin. Use one per time-aligned chart. */
export function getChartCursorSyncConfig(
  chartId: ChartId,
  getTimestampForCursor: (u: uPlot, idx: number) => number | null
): ChartCursorSyncConfig {
  return {
    chartId,
    getTimestampForCursor,
    setChartCursorTimestamp: (ts) => useAppStore.getState().setChartCursorTimestamp(ts),
    setChartCursorFraction: (f) => useAppStore.getState().setChartCursorFraction(f),
    setChartHoveredBy: (id) => useAppStore.getState().setChartHoveredBy(id),
  };
}

export function chartPanPlugin(
  onScaleChange?: (min: number, max: number) => void,
  onAfterPan?: (u: uPlot, newMin: number, newMax: number) => void,
  cursorSync?: ChartCursorSyncConfig
) {
  let isPanning = false;
  let startLeft = 0;
  let startMin = 0;
  let startMax = 0;
  let prevCursor = '';
  let panChart: uPlot | null = null;
  let docMove: ((e: MouseEvent) => void) | null = null;
  let docUp: ((e: MouseEvent) => void) | null = null;

  function plotLeft(u: uPlot, e: MouseEvent): number {
    const r = u.rect;
    return e.clientX - r.left;
  }

  function doPan(u: uPlot, e: MouseEvent) {
    const left = plotLeft(u, e);
    const startVal = u.posToVal(startLeft, 'x', false);
    const currentVal = u.posToVal(left, 'x', false);
    const deltaVal = startVal - currentVal;
    const n = u.data[0].length - 1;
    const windowSize = startMax - startMin;
    const newMin = Math.max(0, Math.min(n - windowSize, startMin + deltaVal));
    const newMax = Math.min(n, newMin + windowSize);
    u.batch(() => {
      u.setScale('x', { min: newMin, max: newMax });
      onAfterPan?.(u, newMin, newMax);
    });
    onScaleChange?.(newMin, newMax);
  }

  function stopPan(u: uPlot) {
    isPanning = false;
    panChart = null;
    u.root.style.cursor = prevCursor;
    if (docMove) document.removeEventListener('mousemove', docMove);
    if (docUp) document.removeEventListener('mouseup', docUp);
    docMove = null;
    docUp = null;
  }

  return {
    opts: (_u: uPlot, baseOpts: uPlot.Options) => {
      baseOpts.select = { show: false, left: 0, top: 0, width: 0, height: 0 };
      const cursor = (baseOpts.cursor ??= {}) as uPlot.Cursor;
      cursor.drag = { x: false, y: false, setScale: false };
      cursor.bind = {
        mousedown: (u, _targ, handler) => (e: MouseEvent) => {
          if (e.button !== 0) return handler?.(e) ?? null;
          const r = u.rect;
          const left = e.clientX - r.left;
          if (left < 0 || left > r.width) return handler?.(e) ?? null;
          isPanning = true;
          panChart = u;
          startLeft = left;
          startMin = u.scales.x.min!;
          startMax = u.scales.x.max!;
          prevCursor = u.root.style.cursor;
          u.root.style.cursor = 'grabbing';

          docMove = (e: MouseEvent) => {
            if (panChart && isPanning) doPan(panChart, e);
          };
          docUp = (e: MouseEvent) => {
            if (e.button === 0 && panChart) stopPan(panChart);
          };
          document.addEventListener('mousemove', docMove);
          document.addEventListener('mouseup', docUp);

          return handler?.(e) ?? null;
        },
        mousemove: (u, _targ, handler) => (e: MouseEvent) => {
          if (isPanning) {
            doPan(u, e);
            return null;
          }
          if (cursorSync) {
            const mouseLeft = Math.max(0, Math.min(u.rect.width, e.clientX - u.rect.left));
            const scaleVal = u.posToVal(mouseLeft, 'x', false);
            const scaleMin = u.scales.x.min ?? 0;
            const scaleMax = u.scales.x.max ?? scaleMin + 1;
            const fraction =
              scaleMax > scaleMin
                ? Math.max(0, Math.min(1, (scaleVal - scaleMin) / (scaleMax - scaleMin)))
                : 0;
            cursorSync.setChartCursorFraction(fraction);
            const idx = u.valToIdx(scaleVal);
            if (idx != null && Number.isFinite(idx)) {
              const roundedIdx = Math.round(idx);
              const ts = cursorSync.getTimestampForCursor(u, roundedIdx);
              if (ts != null) cursorSync.setChartCursorTimestamp(ts);
              u.setLegend({ idx: roundedIdx }, false);
            }
            const top = e.clientY - u.rect.top;
            u.setCursor({ left: mouseLeft, top }, false);
            u.redraw(); // so plugins (e.g. trade markers hover annotation) can read cursor and redraw
            return null; // don't run uPlot's default handler (it would set cursor to mouse position)
          }
          return handler?.(e) ?? null;
        },
        mouseup: (u, _targ, handler) => (e: MouseEvent) => {
          if (e.button === 0 && isPanning) {
            stopPan(u);
            return null;
          }
          return handler?.(e) ?? null;
        },
        mouseleave: (u, _targ, handler) => (e: MouseEvent) => {
          if (cursorSync) {
            cursorSync.setChartHoveredBy(null);
            cursorSync.setChartCursorTimestamp(null);
            cursorSync.setChartCursorFraction(null);
          }
          u.setCursor({ left: -1, top: -1 }, false);
          u.redraw(); // clear hover annotation when pointer leaves chart
          if (!isPanning) u.root.style.cursor = '';
          return handler?.(e) ?? null;
        },
        mouseenter: (u, _targ, handler) => (e: MouseEvent) => {
          if (cursorSync) cursorSync.setChartHoveredBy(cursorSync.chartId);
          if (!isPanning) u.root.style.cursor = 'grab';
          return handler?.(e) ?? null;
        },
      };
    },
    hooks: {},
  };
}

const X_AXIS_SECOND_ROW_CLASS = 'u-x-axis-second-row';

const X_AXIS_SECOND_ROW_HEIGHT = 56;

/**
 * Plugin: draws second row of x-axis labels (month names) below the day row when mode is dayMonthTwoRow.
 * Uses an overlay div at the bottom of the chart (same position as x-axis) so labels are not clipped.
 */
export function xAxisSecondRowPlugin(timestamps: number[]) {
  let overlay: HTMLDivElement | null = null;

  function ensureOverlay(u: uPlot): HTMLDivElement {
    if (overlay && u.root.contains(overlay)) return overlay;
    overlay = document.createElement('div');
    overlay.className = X_AXIS_SECOND_ROW_CLASS;
    overlay.setAttribute('aria-hidden', 'true');
    overlay.style.cssText =
      'position:absolute;left:0;bottom:0;right:0;pointer-events:none;overflow:visible;z-index:10;';
    u.root.style.position = 'relative';
    u.root.appendChild(overlay);
    return overlay;
  }

  function draw(u: uPlot) {
    const mode = getXAxisMode(timestamps, u.scales.x.min ?? 0, u.scales.x.max ?? 0);
    const el = ensureOverlay(u);
    el.style.display = mode === 'dayMonthTwoRow' ? 'block' : 'none';
    el.style.height = X_AXIS_SECOND_ROW_HEIGHT + 'px';

    if (mode !== 'dayMonthTwoRow') {
      el.innerHTML = '';
      return;
    }

    const axis = u.axes[0];
    const splits: number[] = (axis as any)._splits;
    if (!splits || splits.length === 0) {
      el.innerHTML = '';
      return;
    }

    // Use CSS pixels: valToPos(..., false) is 0..plotWidth; bbox is in device pixels so convert for plot left.
    const pxRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const plotLeftCss = u.bbox.left / pxRatio;
    // Second row: sit just below the day labels (first row). Slightly higher = closer to "2".
    const monthRowBottomPx = 40;
    const parts: string[] = [];
    for (let i = 0; i < splits.length; i++) {
      const idx = splits[i];
      if (!isMonthBoundary(timestamps, idx)) continue;
      const label = monthChangeLabel(timestamps, idx);
      if (!label) continue;
      const xPx = plotLeftCss + u.valToPos(idx, 'x', false);
      parts.push(
        `<span style="position:absolute;left:${xPx}px;bottom:${monthRowBottomPx}px;transform:translateX(-50%);font-size:11px;color:#1c1c1c;font-weight:600;white-space:nowrap;">${label}</span>`
      );
    }
    el.innerHTML = parts.join('');
  }

  return {
    hooks: { draw },
  };
}

export default function PrimarySeriesChart() {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<uPlot | null>(null);
  const timestampsRef = useRef<number[]>([]);

  const primaryInstrumentId = useAppStore((s) => s.primaryInstrumentId);
  const instruments = useAppStore((s) => s.instruments);
  const ohlc = useAppStore((s) => s.ohlc);
  const ohlcLoading = useAppStore((s) => s.ohlcLoading);
  const backtestEquityCurve = useAppStore((s) => s.backtestEquityCurve);
  const backtestResult = useAppStore((s) => s.backtestResult);
  const selectedTradeSetKey = useAppStore((s) => s.selectedTradeSetKey);
  const selectedTradeIndex = useAppStore((s) => s.selectedTradeIndex);
  const chartTimeSync = useAppStore((s) => s.chartTimeSync);
  const chartTimeSyncPreset = useAppStore((s) => s.chartTimeSyncPreset);
  const setChartTimeRange = useAppStore((s) => s.setChartTimeRange);
  const setChartTimeSyncPreset = useAppStore((s) => s.setChartTimeSyncPreset);
  const setChartVisibleTimeRange = useAppStore((s) => s.setChartVisibleTimeRange);
  const primaryChartDisplayAdjusted = useAppStore((s) => s.primaryChartDisplayAdjusted);
  const setPrimaryChartDisplayAdjusted = useAppStore((s) => s.setPrimaryChartDisplayAdjusted);
  const indicators = useAppStore((s) => s.indicators);
  const indicatorSeries = useAppStore((s) => s.indicatorSeries);
  useSyncedCrosshair(
    'primary',
    chartRef,
    timestampsRef,
    (ts) => findIndexForTimestamp(timestampsRef.current, ts ?? 0) ?? undefined
  );

  useEffect(() => {
    if (!containerRef.current || !primaryInstrumentId || !ohlc) return;

    const instrumentAdjusted = instruments.find((i) => i.id === primaryInstrumentId)?.adjusted ?? true;
    const displayAdjusted = primaryChartDisplayAdjusted ?? instrumentAdjusted;
    const rawPrimary = buildUplotData(ohlc, primaryInstrumentId, displayAdjusted);
    if (!rawPrimary || rawPrimary.data[0].length === 0) return;

    const equityTimestamps =
      backtestEquityCurve?.timestamps.map((ts) => toUnixSeconds(ts)) ?? [];
    const instrumentIds = instruments.map((i) => i.id);
    const timestamps = getCanonicalTimestampsForAllCharts(
      ohlc,
      primaryInstrumentId,
      instrumentIds,
      equityTimestamps.length > 0 ? equityTimestamps : undefined
    );
    timestampsRef.current = timestamps;

    const splitChartData =
      !displayAdjusted ? getSplitChartData(ohlc, primaryInstrumentId, timestamps) : [];

    const open = padSeriesToTimeline(
      timestamps,
      rawPrimary.timestamps,
      rawPrimary.data[1] as number[],
      null
    );
    const high = padSeriesToTimeline(
      timestamps,
      rawPrimary.timestamps,
      rawPrimary.data[2] as number[],
      null
    );
    const low = padSeriesToTimeline(
      timestamps,
      rawPrimary.timestamps,
      rawPrimary.data[3] as number[],
      null
    );
    const close = padSeriesToTimeline(
      timestamps,
      rawPrimary.timestamps,
      rawPrimary.data[4] as number[],
      null
    );
    const vol = padSeriesToTimeline(
      timestamps,
      rawPrimary.timestamps,
      rawPrimary.data[5] as number[],
      null
    );

    const primarySymbol =
      instruments.find((i) => i.id === primaryInstrumentId)?.symbol ?? 'Primary';

    const overlayData: (number | null)[][] = [];
    const overlaySeries: uPlot.Series[] = [];
    const seriesMap = indicatorSeries ?? {};
    const indicatorSeriesKeys = Object.keys(seriesMap);
    const overlayCandidates = indicators.filter(
      (ind) =>
        ind.instrumentId === primaryInstrumentId &&
        getChartBehavior(ind.type) === 'overlay' &&
        seriesMap[ind.id]
    );
    console.log('[PrimarySeriesChart] indicators=', indicators.length, 'indicatorSeries keys=', indicatorSeriesKeys, 'overlayCandidates=', overlayCandidates.length, overlayCandidates.map((c) => ({ id: c.id, type: c.type, hasData: !!seriesMap[c.id] })));
    overlayCandidates.forEach((ind) => {
        const raw = seriesMap[ind.id];
        // Use volume scale when indicator type defaults to volume, or when its input field is volume (e.g. SMA on Volume)
        const typeScale = getIndicatorChartScale(ind.type);
        const field = (ind.config?.field ?? ind.config?.finalField ?? 'close').toString().toLowerCase();
        const scale = typeScale === 'volume' || field === 'volume' ? 'vol' : 'y';
        const displayInfo = getIndicatorDisplayInfo(ind.type);
        const emits = displayInfo?.emits ?? { Value: 'Number' };
        const fieldKeys = Object.keys(emits);
        const backendFields =
          fieldKeys.length > 1
            ? fieldKeys.map((k) => k.toLowerCase())
            : [undefined];
        backendFields.forEach((field, fi) => {
          const values = getIndicatorValuesOnTimeline(raw, timestamps, field);
          overlayData.push(values);
          const label =
            fieldKeys.length > 1
              ? `${getIndicatorDisplayName(ind, primarySymbol)} ${fieldKeys[fi]}`
              : getIndicatorDisplayName(ind, primarySymbol);
          overlaySeries.push({
            label,
            scale,
            stroke: displayInfo?.colors?.[fieldKeys[fi]] ?? displayInfo?.color ?? '#4a6572',
            width: displayInfo?.lineWidth ?? 2,
            spanGaps: true,
            value: (_u, rawVal) =>
              rawVal != null && Number.isFinite(rawVal as number)
                ? (rawVal as number).toFixed(4)
                : '--',
          });
        });
      });
    console.log('[PrimarySeriesChart] overlayData series count=', overlayData.length, 'overlaySeries count=', overlaySeries.length);

    const data: uPlot.AlignedData = [
      Array.from({ length: timestamps.length }, (_, i) => i),
      open,
      high,
      low,
      close,
      vol,
      ...overlayData,
    ];
    const width = containerRef.current.clientWidth;
    const height = 400;

    const backendTimestamps = backtestResult?.timestamps ?? [];
    const getChartIndexForBackendBar = (backendBarIndex: number): number =>
      backendBarToChartIndex(backendBarIndex, backendTimestamps, timestamps);

    const opts: uPlot.Options = {
      width,
      height,
      plugins: [
        ohlcBarsPlugin({ upColor: UP_COLOR, downColor: DOWN_COLOR }),
        tradeMarkersPlugin({
          getTrades: () => {
            const result = useAppStore.getState().backtestResult;
            const key = useAppStore.getState().selectedTradeSetKey;
            if (!result) return [];
            // Exit expression and expression hold use expression_hold_trades (same as BacktestTradeList)
            if (Array.isArray(result.expression_hold_trades))
              return result.expression_hold_trades;
            return key === 'exit'
              ? result.exit_on_first_profit_trades
              : result.constant_hold_trades[parseInt(key, 10)] ?? [];
          },
          getSelectedTradeIndex: () => useAppStore.getState().selectedTradeIndex,
          getChartIndexForBackendBar,
          volumeStripRatio: VOLUME_STRIP_RATIO,
          getEntryPriceForMarker: (trade) => {
            const state = useAppStore.getState();
            const displayAdjusted =
              state.primaryChartDisplayAdjusted ??
              state.instruments.find((i) => i.id === state.primaryInstrumentId)?.adjusted ??
              true;
            return displayAdjusted && trade.entry_price_adj != null
              ? trade.entry_price_adj
              : trade.entry_price;
          },
          getExitPriceForMarker: (trade) => {
            const state = useAppStore.getState();
            const displayAdjusted =
              state.primaryChartDisplayAdjusted ??
              state.instruments.find((i) => i.id === state.primaryInstrumentId)?.adjusted ??
              true;
            return displayAdjusted && trade.exit_price_adj != null
              ? trade.exit_price_adj
              : trade.exit_price;
          },
        }),
        ...(splitChartData.length > 0
          ? [
              splitAnnotationPlugin({
                getSplitData: () => splitChartData,
                volumeStripRatio: VOLUME_STRIP_RATIO,
              }),
            ]
          : []),
        chartPanPlugin(
          (min, max) => useAppStore.getState().setChartTimeRange(min, max),
          (u, newMin, newMax) => {
            const [yMin, yMax] = visiblePriceRange(u, undefined, undefined, newMin, newMax);
            u.setScale('y', { min: yMin, max: yMax });
            const [, volMax] = visibleVolRange(u, undefined, undefined, newMin, newMax);
            u.setScale('vol', { min: 0, max: volMax });
          },
          getChartCursorSyncConfig('primary', (_u, idx) => timestamps[idx] ?? null)
        ),
        xAxisSecondRowPlugin(timestamps),
      ],
      scales: {
        x: { distr: 2 },
        y: {
          auto: true,
          range: (u, dataMin, dataMax) => visiblePriceRange(u, dataMin, dataMax),
        },
        vol: {
          auto: true,
          range: (u, _dataMin, dataMax) => visibleVolRange(u, _dataMin, dataMax),
        },
      },
      series: [
        {
          label: 'Date',
          value: (_u, _rawValue, _seriesIdx, idx) =>
            idx != null ? formatIndexAsDate(timestamps, idx) : '--',
        },
        { label: 'Open', scale: 'y' },
        { label: 'High', scale: 'y' },
        { label: 'Low', scale: 'y' },
        { label: 'Close', scale: 'y' },
        { label: 'Volume', scale: 'vol' },
        ...overlaySeries,
      ],
      axes: [
        createXAxisConfig(timestamps),
        {
          side: 1,
          scale: 'y',
          size: CHART_RIGHT_AXIS_SIZE,
          values: (_u, vals) => vals.map((v) => (v != null ? v.toFixed(2) : '')),
        },
      ],
    };

    const chart = new uPlot(opts, data, containerRef.current);
    chartRef.current = chart;
    const n = data[0].length;
    const lastIdx = n - 1;
    const sync = useAppStore.getState().chartTimeSync;
    const preset = useAppStore.getState().chartTimeSyncPreset;
    const canRestore =
      preset !== 'full' &&
      sync.xMin != null &&
      sync.xMax != null &&
      sync.xMin < sync.xMax;

    let xRange: { min: number; max: number };
    if (canRestore) {
      const xMin = sync.xMin ?? 0;
      const xMax = sync.xMax ?? 0;
      const min = Math.max(0, Math.min(xMin, lastIdx));
      const max = Math.max(0, Math.min(xMax, lastIdx));
      if (min < max) {
        xRange = { min, max };
        chart.setScale('x', { min, max });
      } else {
        xRange = applyRange(chart, n, 'full');
        useAppStore.getState().setChartTimeSyncPreset('full');
      }
    } else {
      xRange = applyRange(chart, n, 'full');
      useAppStore.getState().setChartTimeSyncPreset('full');
    }
    useAppStore.getState().setChartTimeRange(xRange.min, xRange.max);
    const minIdx = Math.min(xRange.min, timestamps.length - 1);
    const maxIdx = Math.min(xRange.max, timestamps.length - 1);
    const minTs = timestamps[minIdx] ?? 0;
    const maxTs = timestamps[maxIdx] ?? 0;
    useAppStore.getState().setChartVisibleTimeRange(minTs, maxTs);
    const [yMin, yMax] = visiblePriceRange(chart, undefined, undefined, xRange.min, xRange.max);
    const [, volMax] = visibleVolRange(chart, undefined, undefined, xRange.min, xRange.max);
    chart.setScale('y', { min: yMin, max: yMax });
    chart.setScale('vol', { min: 0, max: volMax });

    useAppStore.getState().setPrimaryChartFullWidth(width);

    const ro = new ResizeObserver(() => {
      if (!containerRef.current || !chartRef.current) return;
      const w = containerRef.current.clientWidth;
      chartRef.current.setSize({ width: w, height });
      useAppStore.getState().setPrimaryChartFullWidth(w);
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.destroy();
      chartRef.current = null;
      useAppStore.getState().setPrimaryChartFullWidth(null);
    };
  }, [
    primaryInstrumentId,
    instruments,
    ohlc,
    backtestEquityCurve,
    backtestResult,
    primaryChartDisplayAdjusted,
    indicators,
    indicatorSeries,
  ]);

  // When a trade is selected and the time window is not "full", center the chart on that trade's entry bar.
  // Use primary chart index space (canonical timeline) so the trade is visible when primary/secondary have different date ranges.
  useEffect(() => {
    if (selectedTradeIndex == null || chartTimeSyncPreset === 'full' || !backtestResult) return;
    const sync = useAppStore.getState().chartTimeSync;
    if (sync.xMin == null || sync.xMax == null) return;
    const chartTimestamps = timestampsRef.current;
    if (chartTimestamps.length === 0) return;
    const selectedTrades = Array.isArray(backtestResult.expression_hold_trades)
      ? backtestResult.expression_hold_trades
      : selectedTradeSetKey === 'exit'
        ? backtestResult.exit_on_first_profit_trades
        : backtestResult.constant_hold_trades[parseInt(selectedTradeSetKey, 10)] ?? [];
    const trade = selectedTrades[selectedTradeIndex];
    if (!trade) return;
    const centerChartIndex = backendBarToChartIndex(
      trade.entry_bar,
      backtestResult.timestamps,
      chartTimestamps
    );
    const totalChartBars = chartTimestamps.length;
    const barCount = sync.xMax - sync.xMin;
    const half = Math.floor(barCount / 2);
    const newMin = Math.max(0, centerChartIndex - half);
    const newMax = Math.min(totalChartBars - 1, centerChartIndex + (barCount - half - 1));
    if (newMin >= newMax) return;
    useAppStore.getState().setChartTimeRange(newMin, newMax);
  }, [selectedTradeIndex, chartTimeSyncPreset, backtestResult, selectedTradeSetKey]);

  // Redraw chart when selection changes so the trade band/markers update without recreating the chart
  useEffect(() => {
    chartRef.current?.redraw();
  }, [selectedTradeIndex, selectedTradeSetKey]);

  // Apply synced x range when another chart (e.g. equity) or pan/range on this chart updates store
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart || chartTimeSync.xMin == null || chartTimeSync.xMax == null) return;
    const { xMin, xMax } = chartTimeSync;
    if (chart.scales.x.min === xMin && chart.scales.x.max === xMax) return;
    const n = chart.data[0].length - 1;
    const min = Math.max(0, Math.min(xMin, n));
    const max = Math.max(0, Math.min(xMax, n));
    if (min >= max) return;
    const timestamps = timestampsRef.current;
    if (timestamps.length > 0) {
      setChartVisibleTimeRange(timestamps[min], timestamps[max]);
    }
    chart.batch(() => {
      chart.setScale('x', { min, max });
      const [yMin, yMax] = visiblePriceRange(chart, undefined, undefined, min, max);
      chart.setScale('y', { min: yMin, max: yMax });
      const [, volMax] = visibleVolRange(chart, undefined, undefined, min, max);
      chart.setScale('vol', { min: 0, max: volMax });
    });
  }, [chartTimeSync.xMin, chartTimeSync.xMax, setChartVisibleTimeRange]);

  const primarySymbol =
    instruments.find((i) => i.id === primaryInstrumentId)?.symbol ?? 'Primary';
  const hasData =
    primaryInstrumentId &&
    ohlc?.[primaryInstrumentId] &&
    Object.keys(ohlc[primaryInstrumentId].series ?? {}).length > 0;

    function handleRangeSelect(preset: ChartTimeSyncPreset) {
    const chart = chartRef.current;
    if (!primaryInstrumentId || !ohlc) return;
    if (!chart) {
      setChartTimeSyncPreset(preset);
      return;
    }
    // Use canonical timeline length (chart data) so Full Range includes all instruments' dates
    const n = chart.data[0].length;
    if (n === 0) return;
    const rightEdgeIndex =
      chart.scales.x.min != null && chart.scales.x.max != null
        ? Math.max(0, Math.min(n - 1, Math.round(chart.scales.x.max)))
        : n - 1;
    const xRange = applyRange(chart, n, preset, rightEdgeIndex);
    const [yMin, yMax] = visiblePriceRange(chart, undefined, undefined, xRange.min, xRange.max);
    const [, volMax] = visibleVolRange(chart, undefined, undefined, xRange.min, xRange.max);
    chart.setScale('y', { min: yMin, max: yMax });
    chart.setScale('vol', { min: 0, max: volMax });
    setChartTimeRange(xRange.min, xRange.max);
    const ts = timestampsRef.current;
    if (ts.length > 0) {
      setChartVisibleTimeRange(
        ts[Math.min(xRange.min, ts.length - 1)],
        ts[Math.min(xRange.max, ts.length - 1)]
      );
    }
    setChartTimeSyncPreset(preset);
  }

  if (!primaryInstrumentId) {
    return (
        <div className="flex items-center justify-center py-12 text-ink/70 text-sm">
        Add an instrument and set it as primary to see the chart.
      </div>
    );
  }

  if (!hasData) {
    if (ohlcLoading) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-ink/80" role="status" aria-live="polite">
          <span className="inline-block h-8 w-8 border-2 border-accent border-t-transparent rounded-full animate-spin mb-3" aria-hidden />
          <p className="text-sm">Loading chart…</p>
        </div>
      );
    }
    return (
      <div className="flex items-center justify-center py-12 text-ink/70 text-sm">
        Run a backtest to load {primarySymbol} data and display the chart.
      </div>
    );
  }

  const displayAdjusted = primaryChartDisplayAdjusted ?? (instruments.find((i) => i.id === primaryInstrumentId)?.adjusted ?? true);

  return (
    <div className="w-full">
      <div className="flex items-center justify-between mb-2">
        <p className="text-sm text-ink/80">{primarySymbol}</p>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <span className="text-xs text-ink/70">Prices:</span>
            {(['adjusted', 'unadjusted'] as const).map((mode) => {
              const isAdjusted = mode === 'adjusted';
              return (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setPrimaryChartDisplayAdjusted(isAdjusted)}
                  className={
                    'px-2 py-1 text-xs rounded-sm border transition-colors ' +
                    (displayAdjusted === isAdjusted
                      ? 'bg-ink text-newsprint border-ink'
                      : 'bg-newsprint-panel text-ink border-rule hover:bg-newsprint-dark')
                  }
                >
                  {mode === 'adjusted' ? 'Adjusted' : 'Unadjusted'}
                </button>
              );
            })}
          </div>
          <div className="flex items-center gap-1">
            {RANGE_PRESETS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => handleRangeSelect(id)}
                className={
                  'px-2 py-1 text-xs rounded-sm border transition-colors ' +
                  (chartTimeSyncPreset === id
                    ? 'bg-ink text-newsprint border-ink'
                    : 'bg-newsprint-panel text-ink border-rule hover:bg-newsprint-dark')
                }
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div ref={containerRef} className="w-full" style={{ height: 400 }} />
    </div>
  );
}
